import { useMemo, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { CreateObjectButton } from '@/components/CreateObjectButton'
import { Building } from 'lucide-react'
import { AuthService } from '@/services/core/auth'
import { EarningsAccountsApi, EarningsAccount, type EarningsAccountInput } from '@/services/earnings/earningsAccountsApi'
import { apiErrorDescription } from '@/services/core/apiError'
import { AppShell } from '../../components/AppShell'
import { EarningsAccountTable } from '../../components/EarningsAccountTable'
import { BulkActionBar } from '../../components/BulkActionBar'
import { StatusFilterButton } from '../../components/StatusFilter'
import { FilterField } from '@/components/FilterButton'
import { ALL_COUNTRIES, CountryFilter } from '@/components/CountryFilter'
import { ListCard } from '../../components/ListCard'
import { ListCardHeader } from '../../components/ListCardHeader'
import { SearchInput } from '../../components/SearchInput'
import { CardContent, CardIcon } from '@/components/ui/card'
import { usePersistedState } from '@/hooks/usePersistedState'
import { useUrlSearch } from '@/hooks/useUrlSearch'
import { useNetwork } from '@/contexts/NetworkContext'
import { ListPagination } from '../../components/ListPagination'
import { PageSizeSelect } from '../../components/PageSizeSelect'

export function EarningsAccountsPage() {
  const { t } = useTranslation()
  const hasToken = AuthService.isAuthenticated()
  const [search, setSearch] = useUrlSearch()
  const { scopeKey, scopeReady } = useNetwork()
  const [statusFilter, setStatusFilter] = usePersistedState<'all' | 'ACTIVE' | 'INACTIVE'>('filters.earningsAccounts.status', 'ACTIVE')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(25)
  // Search and state go to the API. The country filter cannot — a publisher's
  // countries are worked out from its sites — so the whole matching list is
  // loaded and paged here, which keeps the country filter exact.
  const { data: accounts = [], isLoading: loading } = useQuery({
    queryKey: ['earningsAccounts', scopeKey, search, statusFilter],
    queryFn: () => EarningsAccountsApi.getAll(search, statusFilter),
    enabled: hasToken && scopeReady,
    staleTime: 5 * 60 * 1000,
  })
  const queryClient = useQueryClient()

  const [selection, setSelection] = useState<EarningsAccount[]>([])
  const [bulkLoading, setBulkLoading] = useState(false)

  // A publisher's countries are the countries of its sites, so one publisher
  // can match several — the filter offers only those present in the list.
  const [countryFilter, setCountryFilter] = usePersistedState<string>('filters.earningsAccounts.country', ALL_COUNTRIES)
  const countryCodes = useMemo(() => accounts.flatMap((a) => a.countries), [accounts])

  const filtered = accounts.filter(
    (a) => countryFilter === ALL_COUNTRIES || a.countries.includes(countryFilter),
  )
  const totalCount = filtered.length
  const pageRows = filtered.slice((page - 1) * pageSize, page * pageSize)

  /** Back to the first page whenever the rows underneath change. */
  const refilter = (apply: () => void) => {
    apply()
    setPage(1)
    setSelection([])
  }

  const bulkUpdate = async (detail: string, payload: Partial<EarningsAccountInput>) => {
    setBulkLoading(true)
    try {
      await Promise.all(selection.map((a) =>
        EarningsAccountsApi.update(a.id, { name: a.name, ...payload })
      ))
      queryClient.invalidateQueries({ queryKey: ['earningsAccounts'] })
      toast.success(t('common.done'), {
        description: t('publishers.doneDetail', { count: selection.length, label: detail }),
        duration: 3000,
      })
      setSelection([])
    } catch (e) {
      toast.error(t('common.error'), { description: apiErrorDescription(e), duration: 5000 })
    } finally {
      setBulkLoading(false)
    }
  }

  return (
    <AppShell crumbs={[{ label: t('publishers.title') }]}>

      <div className="ad-page">
        <div className="ad-page__header">
          <div className="ad-page__title-block">
            <h1>{t('publishers.title')}</h1>
            <p>{t('publishers.subtitle')}</p>
          </div>
          <div className="ad-page__actions">
            <CreateObjectButton type="earningsAccount" size="default" />
          </div>
        </div>

        <ListCard>
          <ListCardHeader
            title={
              <>
                <CardIcon icon={Building} />
                {t('publishers.title')}
              </>
            }
            count={totalCount}
            actions={<PageSizeSelect value={pageSize} onChange={(size) => refilter(() => setPageSize(size))} />}
            controls={
              <>
                <SearchInput value={search} onChange={(v) => refilter(() => setSearch(v))} />
                <StatusFilterButton
                  value={statusFilter}
                  onChange={(v) => refilter(() => setStatusFilter(v))}
                  extraActiveCount={countryFilter !== ALL_COUNTRIES ? 1 : 0}
                  onReset={() =>
                    refilter(() => {
                      setStatusFilter('ACTIVE')
                      setCountryFilter(ALL_COUNTRIES)
                    })
                  }
                >
                  <FilterField label={t('common.country')}>
                    <CountryFilter
                      value={countryFilter}
                      onChange={(v) => refilter(() => setCountryFilter(v))}
                      codes={countryCodes}
                    />
                  </FilterField>
                </StatusFilterButton>
              </>
            }
          />

          <BulkActionBar
            count={selection.length}
            actions={[
              { label: t('common.activate'), icon: 'pi pi-check-circle', severity: 'success', onClick: () => bulkUpdate(t('common.activate'), { objectState: 'ACTIVE' }) },
              { label: t('common.deactivate'), icon: 'pi pi-ban', severity: 'danger', onClick: () => bulkUpdate(t('common.deactivate'), { objectState: 'INACTIVE' }) },
              { label: t('publishers.allowPoliticalAds'), icon: 'pi pi-flag', severity: 'info', onClick: () => bulkUpdate(t('publishers.allowPoliticalAds'), { politicalAdvertising: 'ALLOWED' }) },
              { label: t('publishers.blockPoliticalAds'), icon: 'pi pi-flag', severity: 'warning', onClick: () => bulkUpdate(t('publishers.blockPoliticalAds'), { politicalAdvertising: 'BLOCKED' }) },
            ]}
            onClear={() => setSelection([])}
            loading={bulkLoading}
          />

          <CardContent className="p-0">
            <EarningsAccountTable
              accounts={pageRows}
              loading={loading}
              selection={selection}
              onSelectionChange={setSelection}
              emptyMessage={
                hasToken ? t('publishers.noPublishersFound') : t('common.signInToLoad')
              }
            />
          </CardContent>

          <ListPagination
            page={page}
            pageSize={pageSize}
            totalCount={totalCount}
            onPageChange={setPage}
            summary={t('publishers.publisherCount', { count: totalCount })}
          />
        </ListCard>
      </div>
    </AppShell>
  )
}
